import { useState, useRef, useEffect } from 'react';
import { Center, Stack, Loader, Text, TextInput, Flex, useMantineTheme } from '@mantine/core';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { updateBatchTitle } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import { ExtractionStagesAccordion } from '@dealstack/frontend/app/pages/Project/Cap/BatchAnalysis/components/ExtractionStagesAccordion';
import { useTransactionFromDocumentModalContext } from '../TransactionFromDocumentModalContext';
import { useParams } from 'react-router-dom';

export const ProcessingState = () => {
	const {
		processingStep,
		currentProcessingIndex: currentDocument,
		documents,
		currentProcessingDocument,
		isExtractionRunning,
		extractionProgress,
		batchTitle,
		setBatchTitle: onBatchTitleChange,
		batchId: controllerBatchId,
		selectedBatchId,
	} = useTransactionFromDocumentModalContext();
	const batchId = controllerBatchId || selectedBatchId;
	const { dealId } = useParams<{ dealId: string }>();
	const totalDocuments = documents.length;
	const currentDocumentName = currentProcessingDocument?.file.name;
	const theme = useMantineTheme();
	const tCapManage = useTranslationCapManage();
	const [isSavingTitle, setIsSavingTitle] = useState(false);
	const [titleSaved, setTitleSaved] = useState(false);
	const titleSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clear the title-saved timer on unmount to prevent state updates after unmount
	useEffect(() => {
		return () => {
			if (titleSavedTimerRef.current) {
				clearTimeout(titleSavedTimerRef.current);
			}
		};
	}, []);

	const handleSaveTitle = async () => {
		if (!batchId || !batchTitle?.trim()) {
			return;
		}
		setIsSavingTitle(true);
		try {
			await updateBatchTitle(batchId, batchTitle.trim(), dealId ?? '');
			setTitleSaved(true);
			if (titleSavedTimerRef.current) {
				clearTimeout(titleSavedTimerRef.current);
			}
			titleSavedTimerRef.current = setTimeout(() => setTitleSaved(false), 2000);
		} catch {
			showToastV2({
				state: 'error',
				title: tCapManage('transactions.add_from_document.error_title'),
				message: tCapManage('transactions.add_from_document.save_batch_title_failed'),
			});
		} finally {
			setIsSavingTitle(false);
		}
	};

	// Pipeline progress display — shared accordion + batch-rename row on top
	if (isExtractionRunning && extractionProgress) {
		const { documents } = extractionProgress;
		return (
			<Center style={{ minHeight: '400px' }}>
				<Stack gap="md" style={{ width: '100%', maxWidth: '600px' }}>
					<Text size="H3Thin" fw={500} ta="center">
						{tCapManage('transactions.add_from_document.processing_uploads')}
					</Text>
					<Flex gap="xs" align="center">
						<TextInput
							placeholder={tCapManage(
								'transactions.add_from_document.rename_batch_title'
							)}
							value={batchTitle || ''}
							onChange={(e) => {
								onBatchTitleChange?.(e.currentTarget.value);
								setTitleSaved(false);
							}}
							style={{ flex: 1 }}
							styles={{
								input: {
									borderColor: theme.colors.gray[4],
									'&:focus': { borderColor: theme.colors.blue[6] },
								},
							}}
						/>
						<ButtonV2
							variant="secondary"
							size="xs"
							buttonTheme="light"
							onClick={handleSaveTitle}
							disabled={!batchId || !batchTitle?.trim() || isSavingTitle}
						>
							{isSavingTitle && tCapManage('common.saving')}
							{!isSavingTitle && titleSaved && tCapManage('common.saved')}
							{!isSavingTitle && !titleSaved && tCapManage('common.save')}
						</ButtonV2>
					</Flex>

					<ExtractionStagesAccordion documents={documents} title="" minHeight={0} />
				</Stack>
			</Center>
		);
	}

	// Fallback: uploading phase (before pipeline starts)
	return (
		<Center style={{ minHeight: '400px' }}>
			<Stack align="center" gap="xl" style={{ width: '100%', maxWidth: '500px' }}>
				<Loader size="xl" />
				<Stack align="center" gap="xs" style={{ width: '100%' }}>
					<Text size="H3Thin" fw={500}>
						{processingStep === 'uploading' &&
							tCapManage('transactions.add_from_document.uploading_documents')}
						{processingStep === 'analyzing' &&
							tCapManage('transactions.add_from_document.starting_analysis')}
						{!processingStep && tCapManage('transactions.add_from_document.processing')}
					</Text>

					{totalDocuments > 1 && (
						<Text size="sm" color={theme.colors.gray[9]}>
							{tCapManage('transactions.add_from_document.uploading_document_of', {
								current: currentDocument,
								total: totalDocuments,
							})}
							{currentDocumentName && ` — ${currentDocumentName}`}
						</Text>
					)}
				</Stack>
			</Stack>
		</Center>
	);
};
